"use client";

/**
 * One bookable service as the website shows it — on the Services tab
 * (BrandServicesSection) and on a collection's page (collections/[slug]).
 * Both read their rows from `websiteServiceRow` (convex/products/websiteServices.ts),
 * so a service looks the same wherever it is drawn.
 *
 * The photo sits in the same portrait box as a product's on the shop grid
 * (3:4, filled and centred, as ShopProductCard draws it). It used to be a
 * 160px strip, which showed a tall bouquet as a thin band across its middle
 * (Petal_Some Ug., Oct 2026).
 *
 * What the service comes with is listed under its description ("Comes with",
 * "Extras"; lib/config/serviceAddOns.ts). It is only said: booking is the same.
 *
 * The BOOK button sits where Buy normally does:
 *  - the service has a booking form → /f/<shareCode>?booking=<productId>
 *    (the submission becomes a booking + service order server-side)
 *  - no form → a WhatsApp message to the shop, written for the visitor
 */

import { BadgeCheck, CalendarCheck, Clock, Gift, Plus } from "lucide-react";
import { serviceAddOnLines, type ServiceAddOn } from "@/lib/config/serviceAddOns";

/** A service row as websiteServiceRow returns it. */
export interface PublicService {
  _id: string;
  slug?: string;
  name: string;
  description?: string;
  price: number;
  hidePrice: boolean;
  hasCustomPrice: boolean;
  imageUrl?: string;
  /** How long it takes; never set on a made-to-order service. */
  durationMinutes?: number;
  /** A bouquet, a chair set-up: no length of time — booked for when it is delivered or collected. */
  madeToOrder: boolean;
  /** Set on a membership plan: how many days it runs. */
  membershipDays?: number;
  /** The booking form's public code, when it has a published one. */
  bookingShareCode?: string;
  /** What it comes with and the extras on offer: name, count and price only. */
  addOns?: ServiceAddOn[];
}

function formatDuration(mins?: number): string | null {
  if (!mins || mins <= 0) return null;
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h} hr${h > 1 ? "s" : ""}`;
}

/** Where a service's Book button goes: its booking form, else WhatsApp, else nowhere. */
export function serviceBookHref(
  svc: Pick<PublicService, "_id" | "name" | "bookingShareCode">,
  { brandName, whatsappNumber }: { brandName: string; whatsappNumber?: string },
): string {
  if (svc.bookingShareCode) {
    return `/f/${svc.bookingShareCode}?booking=${svc._id}`;
  }
  const digits = (whatsappNumber || "").replace(/[^\d]/g, "");
  const msg = encodeURIComponent(`Hello ${brandName}, I'd like to book "${svc.name}". When are you available?`);
  return digits ? `https://wa.me/${digits}?text=${msg}` : `#`;
}

export function ServiceCard({
  service: svc,
  brandName,
  whatsappNumber,
  currency = "UGX",
}: {
  service: PublicService;
  brandName: string;
  /** WhatsApp number for the no-form fallback (digits, any format). */
  whatsappNumber?: string;
  currency?: string;
}) {
  const dur = formatDuration(svc.durationMinutes);
  const isMembership = !!svc.membershipDays && svc.membershipDays > 0;
  const bookHref = serviceBookHref(svc, { brandName, whatsappNumber });
  const priceOnRequest = svc.hasCustomPrice || svc.hidePrice;
  // A service sold by quote lists no extras, as a product's page offers none.
  const addOns = serviceAddOnLines(svc.addOns, { currency, showPrices: !priceOnRequest });
  return (
    <div
      className="group rounded-2xl overflow-hidden bg-white transition-shadow hover:shadow-lg flex flex-col"
      style={{ border: "1px solid #e2e8f0" }}
    >
      {/* Visual: photo in the product grid's portrait box when the brand added one, calm gradient otherwise */}
      {svc.imageUrl ? (
        <div className="relative aspect-[3/4] overflow-hidden bg-gray-50">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={svc.imageUrl}
            alt={svc.name}
            className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
          />
        </div>
      ) : (
        <div
          className="h-24 flex items-center justify-center"
          style={{ background: "linear-gradient(135deg, #0f172a 0%, #334155 100%)" }}
        >
          <CalendarCheck className="w-8 h-8 text-white/80" />
        </div>
      )}

      <div className="p-4 sm:p-5 flex flex-col flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="text-sm sm:text-base font-bold leading-snug" style={{ color: "#0f172a" }}>
            {svc.name}
          </h3>
          {isMembership && (
            <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "rgba(147,51,234,0.1)", color: "#7c3aed" }}>
              <BadgeCheck className="w-3 h-3" />
              Membership
            </span>
          )}
        </div>

        <div className="flex items-center gap-2 mt-1.5">
          {/* Made to order has no length to show: it is booked for a delivery or collection time. */}
          {svc.madeToOrder ? (
            <span className="inline-flex items-center text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#f1f5f9", color: "#475569" }}>
              Made to order
            </span>
          ) : (
            dur && (
              <span className="inline-flex items-center gap-1 text-[11px] font-semibold" style={{ color: "#64748b" }}>
                <Clock className="w-3 h-3" />
                {dur}
              </span>
            )
          )}
          {isMembership && (
            <span className="text-[11px] font-semibold" style={{ color: "#64748b" }}>
              {svc.membershipDays} days
            </span>
          )}
        </div>

        {svc.description && (
          <p className="text-xs mt-2 line-clamp-2" style={{ color: "#64748b" }}>
            {svc.description}
          </p>
        )}

        {(addOns.comesWith || addOns.extras) && (
          <div className="mt-2.5 space-y-1 text-[11px] leading-snug" style={{ color: "#475569" }}>
            {addOns.comesWith && (
              <p className="flex items-start gap-1.5">
                <Gift className="w-3 h-3 mt-[2px] shrink-0" aria-hidden />
                <span className="line-clamp-2">
                  <span className="font-semibold" style={{ color: "#0f172a" }}>Comes with:</span> {addOns.comesWith}
                </span>
              </p>
            )}
            {addOns.extras && (
              <p className="flex items-start gap-1.5">
                <Plus className="w-3 h-3 mt-[2px] shrink-0" aria-hidden />
                <span className="line-clamp-2">
                  <span className="font-semibold" style={{ color: "#0f172a" }}>Extras:</span> {addOns.extras}
                </span>
              </p>
            )}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-auto pt-4">
          <span className="text-sm sm:text-base font-black" style={{ color: "#0f172a" }}>
            {priceOnRequest
              ? "Enquire for price"
              : `${currency} ${svc.price.toLocaleString()}`}
          </span>
          <a
            href={bookHref}
            target={svc.bookingShareCode ? undefined : "_blank"}
            rel={svc.bookingShareCode ? undefined : "noopener noreferrer"}
            className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-bold text-white transition-colors"
            style={{ backgroundColor: "#0f172a" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#334155"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.backgroundColor = "#0f172a"; }}
          >
            <CalendarCheck className="w-3.5 h-3.5" />
            Book
          </a>
        </div>
      </div>
    </div>
  );
}
